import { nanoid } from 'nanoid'

function createStorageHelpers<K extends string>(storage: Storage) {
  return {
    set: (key: K, value: string) => storage.setItem(key, value),
    get: (key: K) => storage.getItem(key),
    getOrSet: (key: K, value: string) => {
      const existing = storage.getItem(key)
      if (existing) return existing
      storage.setItem(key, value)
      return value
    },
    remove: (key: K) => storage.removeItem(key),
  }
}

const localStore = createStorageHelpers<
  | 'BrandsWebJWT'
  | 'OnboardingGuid'
  | 'RememberBrowserGuid'
  | 'version'
  | 'bfp'
  | 'BrandsDontShowReportConfirm'
  | 'employee_table_columns_model'
  | 'powergrid_showJobDesc'
>(localStorage)

const sessionStore = createStorageHelpers<
  'BrandsWebJWT' | 'BrandsWebJWTGuid' | 'BrandsWebIsNative' | 'lastReloadTime' | 'sfp' | 'empnum'
>(sessionStorage)

interface EmployeeIdData {
  conum?: string
  empnum?: string
}

export const localStorageService = {
  // TODO: Migrate flat methods (getJwtToken, setJwtToken, etc.) to nested pattern
  // for better discoverability. See powerGridShowJobDesc for example.

  setJwtToken: (token: string) => localStore.set('BrandsWebJWT', token),
  getJwtToken: () => localStore.get('BrandsWebJWT'),
  removeJwtToken: () => localStore.remove('BrandsWebJWT'),
  setOnboardingGuid: (id: string) => localStore.set('OnboardingGuid', id),
  getOnboardingGuid: () => localStore.get('OnboardingGuid'),
  removeOnboardingGuid: () => localStore.remove('OnboardingGuid'),
  setRememberBrowserGuid: (id: string) => localStore.set('RememberBrowserGuid', id),
  getRememberBrowserGuid: () => localStore.get('RememberBrowserGuid'),
  removeRememberBrowserGuid: () => localStore.remove('RememberBrowserGuid'),
  setVersion: (version: string) => localStore.set('version', version),
  getVersion: () => localStore.get('version'),
  getBfp: () => localStore.getOrSet('bfp', nanoid()),
  setDontShowReportConfirm: () => localStore.set('BrandsDontShowReportConfirm', 'true'),
  getDontShowReportConfirm: () => localStore.get('BrandsDontShowReportConfirm') === 'true',
  setEmployeesTableColumnModel: (model?: unknown) => {
    if (model) {
      localStore.set('employee_table_columns_model', JSON.stringify(model))
    } else {
      localStore.remove('employee_table_columns_model')
    }
  },
  getEmployeesTableColumnModel: () => {
    const data = localStore.get('employee_table_columns_model')
    return data ? (JSON.parse(data) as unknown) : null
  },
  powerGridShowJobDesc: {
    getValue: () => (localStore.get('powergrid_showJobDesc') ?? 'true') === 'true',
    setValue: (show: boolean) => {
      localStore.set('powergrid_showJobDesc', show ? 'true' : 'false')
    },
  },
}

export const sessionStorageService = {
  setJwtToken: (token: string) => sessionStore.set('BrandsWebJWT', token),
  getJwtToken: () => sessionStore.get('BrandsWebJWT'),
  removeJwtToken: () => sessionStore.remove('BrandsWebJWT'),
  setJwtGuid: (jwtGuid: string) => sessionStore.set('BrandsWebJWTGuid', jwtGuid),
  getJwtGuid: () => sessionStore.get('BrandsWebJWTGuid'),
  setIsNative: () => sessionStore.set('BrandsWebIsNative', 'true'),
  getIsNative: () => sessionStore.get('BrandsWebIsNative') === 'true',
  setLastReloadTime: (time: number) => sessionStore.set('lastReloadTime', time.toString()),
  getLastReloadTime: () => {
    const data = sessionStore.get('lastReloadTime')
    return data ? Number(data) : null
  },
  getSfp: () => sessionStore.getOrSet('sfp', nanoid()),
  setEmployeeId: (data: EmployeeIdData) => sessionStore.set('empnum', JSON.stringify(data)),
  getEmployeeId: () => {
    const data = sessionStore.get('empnum')
    return data ? (JSON.parse(data) as EmployeeIdData) : null
  },
  removeEmployeeId: () => sessionStore.remove('empnum'),
}
